import React from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { createSupabaseBrowserClient } from '@/client/supabase'
import { Card } from '@/components/ui/card'
import { toast } from 'sonner'
import { Header } from '@/components/Header'
import * as Sentry from '@sentry/react-native'
import { Redirect, useLocalSearchParams, useRouter } from 'expo-router'
import { cn } from '@/lib/utils'
import { useClientSession } from '@/hooks/useClientSession'

export default function LoginPage() {
  const [isLoadingForm, setIsLoadingForm] = React.useState(false)
  const [isEmailSent, setIsEmailSent] = React.useState(false)
  const local = useLocalSearchParams<{ email?: string; 'email-sent'?: string }>()
  const [email, setEmail] = React.useState(local.email ?? '')
  const [code, setCode] = React.useState('')
  const router = useRouter()
  const didSendEmail = local['email-sent'] === 'true'

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoadingForm(true)
    const { error } = await createSupabaseBrowserClient().auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        emailRedirectTo: window.location.origin + '/auth/confirm',
      },
    })
    if (!error) {
      setIsEmailSent(true)
      toast.success('Check your email for the login code (including the junk folder).')
    } else {
      Sentry.captureException(error)
      toast.error(`Failed to send login email (${error.message}). Please try again.`)
    }
    setIsLoadingForm(false)
  }

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoadingForm(true)
    const { error } = await createSupabaseBrowserClient().auth.verifyOtp({
      email,
      token: code,
      type: 'email',
    })
    if (!error) {
      router.push('/configure-sub')
    } else {
      Sentry.captureException(error)
      toast.error(`Failed to verify code (${error.message}). Please try again.`)
    }
    setIsLoadingForm(false)
  }

  const { data, isLoading } = useClientSession()

  if (isLoading) {
    return null
  }

  if (data?.user) {
    return <Redirect href="/configure-sub" />
  }

  return (
    <div className="min-h-screen w-full flex flex-col bg-background">
      <Header />
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-6">
          <h1 className="text-2xl font-bold text-center mb-6">Subscription Login</h1>
          <div className="relative min-h-[200px] overflow-hidden">
            <div
              className={cn(
                'absolute inset-0 px-px transition-all duration-300 ease-in-out',
                isEmailSent ? 'opacity-0 -translate-x-full' : 'opacity-100 translate-x-0',
              )}
            >
              <form onSubmit={handleSendEmail} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" variant="outline" className="w-full" disabled={isLoadingForm}>
                  {isLoadingForm ? 'Sending...' : 'Send login code'}
                </Button>
              </form>
            </div>

            <div
              className={cn(
                'absolute inset-0 px-px transition-all duration-300 ease-in-out',
                isEmailSent ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full',
              )}
            >
              <form onSubmit={handleVerifyCode} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="code">One-time Code</Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="Enter the code from your email"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    autoComplete={'one-time-code'}
                  />
                </div>
                <Button type="submit" variant="outline" className="w-full" disabled={isLoadingForm}>
                  {isLoadingForm ? 'Verifying...' : 'Verify code'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setIsEmailSent(false)
                    setCode('')
                  }}
                >
                  Use a different email
                </Button>
              </form>
            </div>
          </div>
          <p className="text-sm text-muted-foreground text-center mt-4">
            New here? You can sign up above and purchase your license in the next screen.
          </p>
        </Card>
      </div>
    </div>
  )
}

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { CheckCircle } from 'lucide-react'

export function EmailSentDialog({ defaultOpen = false }) {
  return (
    <AlertDialog defaultOpen={defaultOpen}>
      <AlertDialogContent className="">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-success flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Success!
          </AlertDialogTitle>
          <AlertDialogDescription>
            Check your email for the login link (including the junk folder).
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction>Got it, thanks!</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
